import {
  formatDate,
  formatPeriod,
  formatPeriodIso,
  getDateOrNull,
} from "@/utils/dateUtils";
import { ServerRequest } from "../../types";
import { getAuthStatus } from "../getAuthStatus";
import { createBackendApiClient } from "./createBackendApiClient";
import {
  LearningActivity,
  LearningOpportunity as ApiLearningOpportunity,
  LearningOpportunityBase as ApiLearningOpportunityBase,
  LearningOpportunityLearner,
  LearningOpportunityLight,
  LearningOpportunityPerson,
  LearningOpportunityStatus,
  LearningOpportunityType,
  LearningOpportunityUserStatus,
  LearningOutcome,
  StudyFormat,
  Tag,
  TopicAndLevel,
  BrowseLearningOpportunity,
  LightWeightLearningOpportunity,
  PassportLearningOpportunity,
  Audience2,
  LearningOpportunityUserData,
} from "./generated";
import {
  mapUserAdmissionData,
  UserAdmissionData,
  UserAdmissionDataWithMicroCredentials,
} from "./learningOpportunityUserAdmission";
import { createImageUrl } from "./mapHelpers";
import { mapOrganisationBase, OrganisationBase } from "./organisation";
import { buildLearningOpportunityUrl } from "@/utils/url";
import dayjs from "dayjs";
import { IncomingMessage } from "http";
import { calculateDaysLeft } from "@/utils/calculateDaysLeft";

/**
 * Minimal information about a learning opportunity.
 * For dropdown, lists, search etc.
 */
export interface LearningOpportunityName {
  id: string;
  name: string;
  studyPeriod: string;
  studyPeriodShort: string;
}

export type LearningOpportunityListItem = {
  type: LearningOpportunityType;
  id: string;
  url: string;
  title: string | null;
  status: LearningOpportunityStatus | null;
  imageUri: string | null;
  imageUriSmall: string | null;
  studyPeriodShort: string | null;
  studyPeriodLong: string | null;
  studyPeriod: string | null;
  studyPeriodIso: string | null;
  userStatus: LearningOpportunityUserStatus | null;
  hostingUniversityId: string | null;
  hostingUniversityName: string | null;
  ects: number | null;
  userAdmissionData: UserAdmissionData | null;
  studyFormat: StudyFormat | null;
  teamsAccessDate: string | null;
  topics: LearningOpportunityTopic[];
  volumeOfLearning: number | null;
  isPastStartDate: boolean | null;
  tags: Tag[] | null;
  topicsWithLevel: TopicAndLevel[] | null;
  applicationPeriodEndDate: string | null;
  maximumNumberOfSeats: number | null;
  numberOfSeatsOccupied: number | null;
  startDate: string | null;
  selfPaced: boolean | null;
  publishedDate: string | null;
};

export interface LearningOpportunityBaseWithMicroCredentials
  extends PassportLearningOpportunities {
  userAdmissionData: UserAdmissionDataWithMicroCredentials | null;

  /**
   * Indices whether micro credentials will be generated for students of this LO.
   */
  generateMicroCredentials: boolean;
  generateDigitalCredentials: boolean;
}

export type TeamMember = {
  displayName: string;
  userId: string | null;
  imageUrl: string | null;
};

export interface LearningOpportunityTopic {
  id: string;
  name: string | null;
}

export type LearningOpportunity = LearningOpportunityListItem & {
  subTitle: string | null;
  introduction: string | null;
  description: string | null;
  participantInformation: string | null;
  applicationPeriod: string | null;
  ects: number | null;
  hostingUniversity: OrganisationBase | null;
  challengeProviderOrganisations: OrganisationBase[];
  associatedPartnerOrganisations: OrganisationBase[];
  lmsUrl: string | null;
  eciuLmsEnabled: boolean;
  pace: number | null;
  teamchers: TeamMember[];
  learners: TeamMember[];
  admissionInformation: string | null;
  admissionProcessInformation: string | null;
  admissionDocumentTypes:
    | {
        id: string;
        name: string;
        description?: string | null;
      }[]
    | null;

  simplifiedAdmissionProcess: boolean | null;
  simplifiedAdmissionProcessWithNoDocuments: boolean | null;
  simplifiedAdmissionProcessAndIsFull: boolean | null;
  learningOutcomes: LearningOutcome[];
  learningActivities: LearningActivity[];
  generateDigitalCredentials: boolean;
  eqfLevelAlternateName: string | null;
  audiences: Audience2[] | null;
  userData?: LearningOpportunityUserData | null;
};

export type Challenge = {
  type: LearningOpportunityType.Challenge;
} & LearningOpportunity;
export type ChallengeListItem = {
  type: LearningOpportunityType.Challenge;
} & LearningOpportunityListItem;

export type MicroModule = {
  type: LearningOpportunityType.MicroModule;
} & LearningOpportunity;
export type MicroModuleListItem = {
  type: LearningOpportunityType.MicroModule;
} & LearningOpportunityListItem;

/** Get LO list */
export const getChallenges = async (): Promise<ChallengeListItem[]> => {
  const data = await getLearningOpportunities();
  return data
    .filter((x) => x.type === LearningOpportunityType.Challenge)
    .map((x) => x as ChallengeListItem);
};

export const getMicroModules = async (): Promise<MicroModuleListItem[]> => {
  const data = await getLearningOpportunities();
  return data
    .filter((x) => x.type === LearningOpportunityType.MicroModule)
    .map((x) => x as MicroModuleListItem);
};

export const getLearningOpportunities = async (
  request?: IncomingMessage
): Promise<LearningOpportunityListItem[]> => {
  const auth = request ? getAuthStatus(request) : undefined;
  const client = createBackendApiClient(auth?.token);
  const data = await client.learningOpportunity_GetAll();
  return data.filter((x) => !!x.type).map(mapLearningOpportunityListItem);
};

/** Get one LO */
export const getChallenge = async (id: string, userRequest?: ServerRequest) => {
  const lo = await getLearningOpportunity(id, userRequest);
  if (lo.type !== LearningOpportunityType.Challenge)
    throw new Error("LO type mismatch");
  else return lo as Challenge;
};

export const getMicroModule = async (
  id: string,
  userRequest?: ServerRequest
) => {
  const lo = await getLearningOpportunity(id, userRequest);
  if (lo.type !== LearningOpportunityType.MicroModule)
    throw new Error("LO type mismatch");
  else return lo as MicroModule;
};

export const getLO = async (id: string, userRequest?: ServerRequest) => {
  const lo = await getLearningOpportunity(id, userRequest);
  return lo;
};
export const getLearningOpportunity = async (
  id: string,
  userRequest?: ServerRequest
): Promise<LearningOpportunity> => {
  const auth = userRequest ? getAuthStatus(userRequest) : undefined;
  const client = createBackendApiClient(auth?.token);
  const res = await client.learningOpportunity_GetById(id);
  return mapLearningOpportunity(res);
};

export const getLearningOpportunityLight = async (
  id: string,
  userRequest?: ServerRequest
): Promise<LearningOpportunityLight> => {
  const auth = userRequest ? getAuthStatus(userRequest) : undefined;
  const client = createBackendApiClient(auth?.token);
  const lo = await client.learningOpportunity_GetByIdLight(id);
  return lo as LearningOpportunityLight;
};

/** mapping functions */
const mapPerson = (
  person: LearningOpportunityLearner | LearningOpportunityPerson
) => {
  return {
    displayName: `${person.firstName} ${person.lastName}`,
    userId: person.azureAdUserId,
    imageUrl: !person.azureAdUserId
      ? null
      : `/api/profilePhoto/${encodeURIComponent(person.azureAdUserId)}`,
  };
};

export const mapLearningOpportunityList = (
  list: ApiLearningOpportunityBase[],
  filterOnStatus?: LearningOpportunityStatus[]
) => {
  const loTypesToInclude = [
    LearningOpportunityType.Challenge,
    LearningOpportunityType.MicroModule,
  ];

  return list
    .filter((x) => x.type && loTypesToInclude.includes(x.type))
    .filter(
      (x) => !filterOnStatus || (x.status && filterOnStatus.includes(x.status))
    )
    .map(mapLearningOpportunityListItem);
};

export const mapLearningOpportunityListItem = (
  data: ApiLearningOpportunityBase
): LearningOpportunityListItem => {
  const typeSpecific = mapTypeSpecific(data);
  if (!typeSpecific) throw new Error("invalid type LO type");

  const startDate = getDateOrNull(data.startDate);
  let isPastStartDate: boolean | null = null;
  const now = dayjs();
  if (startDate && now.isAfter(startDate)) {
    isPastStartDate = true;
  }

  return {
    ...data,
    type: typeSpecific.type,
    url: buildLearningOpportunityUrl(data.id, data.name),
    title: data.name,
    status: data.status,
    imageUri: createImageUrl(data.imagesPath, 280, 280),
    imageUriSmall: createImageUrl(data.imagesPath, 40, 40),
    studyPeriod: formatPeriod(
      "long",
      getDateOrNull(data.startDate),
      getDateOrNull(data.endDate)
    ),
    studyPeriodLong: formatPeriod(
      "long",
      getDateOrNull(data.startDate),
      getDateOrNull(data.endDate)
    ),
    studyPeriodShort: formatPeriod(
      "short",
      getDateOrNull(data.startDate),
      getDateOrNull(data.endDate)
    ),
    studyPeriodIso: formatPeriodIso(
      getDateOrNull(data.startDate),
      getDateOrNull(data.endDate)
    ),
    userStatus: data.userData?.status ?? null,
    hostingUniversityId: data.hostingUniversity
      ? data.hostingUniversity.id
      : null,
    hostingUniversityName: data.hostingUniversity?.name ?? null,
    userAdmissionData: mapUserAdmissionData(data.userData),
    teamsAccessDate: formatDate("long", data.teamsAccessDate, true) ?? null,
    topics:
      data.topics === null
        ? []
        : data.topics.map((x) => ({
            id: x.id,
            name: x.name,
          })),
    isPastStartDate: isPastStartDate,
    tags:
      data.tags === undefined || data.tags === null
        ? []
        : data.tags.map((x) => ({
            id: x.id,
            name: x.name,
          })),
    topicsWithLevel:
      data.topicsWithLevel === null
        ? []
        : data.topicsWithLevel.map((x) => ({
            topicId: x.topicId,
            levelId: x.levelId,
          })),
    numberOfSeatsOccupied: data.numberOfSeatsOccupied,
    maximumNumberOfSeats: data.maximumNumberOfSeats,
    selfPaced: data.selfPaced,
    publishedDate: data.publishedDate,
  };
};

export const mapLearningOpportunity = (
  data: ApiLearningOpportunity
): LearningOpportunity => {
  const listItemProperties = mapLearningOpportunityListItem(data);

  return {
    ...listItemProperties,
    subTitle: data.subTitle,
    introduction: data.introduction,
    description: data.description,
    participantInformation: data.participantInformation,
    applicationPeriod: formatPeriod(
      "long",
      getDateOrNull(data.applicationPeriodStartDate),
      getDateOrNull(data.applicationPeriodEndDate)
    ),
    studyPeriod: formatPeriod(
      "long",
      getDateOrNull(data.startDate),
      getDateOrNull(data.endDate)
    ),
    studyFormat: data.studyFormat,
    hostingUniversity: data.hostingUniversity
      ? mapOrganisationBase(data.hostingUniversity)
      : null,
    associatedPartnerOrganisations:
      data.associatedPartnerOrganisations.map(mapOrganisationBase),
    challengeProviderOrganisations:
      data.challengeProviderOrganisations.map(mapOrganisationBase),
    ects: data.ects,
    lmsUrl: data.lmsUrl,
    eciuLmsEnabled: data.eciulmsEnabled ?? false,
    pace: data.pace,
    teamchers: data.teamchers.map(mapPerson),
    learners: data.learners.map(mapPerson),

    admissionInformation: data.admissionInformation ?? null,
    admissionProcessInformation: data.admissionProcessInformation ?? null,
    admissionDocumentTypes:
      data.documentTypes?.map((x) => ({
        id: x.id ?? "",
        name: x.name ?? "",
        description: x.description,
      })) ?? [],

    simplifiedAdmissionProcess: data.simplifiedAdmissionProcess,
    simplifiedAdmissionProcessAndIsFull:
      data.usesSimplifiedAdmissionProcessAndAdmissionIsFull,
    simplifiedAdmissionProcessWithNoDocuments:
      data.simplifiedAdmissionProcess === true &&
      data.documentTypes?.length === 0,
    learningOutcomes: data.learningOutcomes ?? [],
    learningActivities: data.learningActivities ?? [],
    applicationPeriodEndDate: data.applicationPeriodEndDate ?? null,
    generateDigitalCredentials: data.generateDigitalCredentials ?? false,
    eqfLevelAlternateName: data.eqfLevelAlternateName,
    audiences: data.audiences ?? [],
  };
};

export const mapTypeSpecific = (data: ApiLearningOpportunityBase) => {
  switch (data.type) {
    case LearningOpportunityType.MicroModule:
      return {
        type: LearningOpportunityType.MicroModule as const,
      };
    case LearningOpportunityType.Challenge:
      return {
        type: LearningOpportunityType.Challenge as const,
      };
  }
};
export const mapTypeSpecificNew = (
  data:
    | PassportLearningOpportunity
    | LightWeightLearningOpportunity
    | BrowseLearningOpportunity
) => {
  switch (data.type) {
    case LearningOpportunityType.MicroModule:
      return {
        type: LearningOpportunityType.MicroModule as const,
      };
    case LearningOpportunityType.Challenge:
      return {
        type: LearningOpportunityType.Challenge as const,
      };
  }
};

function statusSortPriority(status: LearningOpportunityStatus | null) {
  switch (status) {
    case LearningOpportunityStatus.OpenForApplication:
      return 0;
    case LearningOpportunityStatus.ComingSoon:
      return 1;
    case LearningOpportunityStatus.ClosedForApplication:
      return 2;
    default:
      return 3;
  }
}
export function sortLearningOpportunitiesByStatus(
  learningOpps: BrowseLearningOpportunities[]
) {
  const _copy = [...learningOpps];
  return _copy.sort((left, right) => {
    const leftPriority = statusSortPriority(left.status);
    const rightPriority = statusSortPriority(right.status);

    if (leftPriority === 0 && rightPriority === 0) {
      const daysLeftLeft = calculateDaysLeft(
        left.applicationPeriodEndDate as string
      );
      const daysLeftRight = calculateDaysLeft(
        right.applicationPeriodEndDate as string
      );

      if (typeof daysLeftLeft !== "number") return 1;
      if (typeof daysLeftRight !== "number") return -1;

      return daysLeftLeft - daysLeftRight;
    }

    if (leftPriority !== rightPriority) {
      return leftPriority - rightPriority;
    }

    if (right.publishedDate && left.publishedDate) {
      return (
        new Date(right.publishedDate).getTime() -
        new Date(left.publishedDate).getTime()
      );
    }

    return 0;
  });
}
export type BrowseLearningOpportunities = {
  id: string;
  studyPeriodShort: string | null;
  studyPeriodLong: string | null;
  tags: Tag[] | null;
  title: string | null;
  status: LearningOpportunityStatus | null;
  selfPaced: boolean | null;
  type: LearningOpportunityType;
  studyFormat: StudyFormat | null;
  topicsWithLevel: TopicAndLevel[] | null;
  hostingUniversityId: string | null;
  startDate: string | null;
  ects: number | null;
  volumeOfLearning: number | null;
  topics: LearningOpportunityTopic[];
  publishedDate: string | null;
  maximumNumberOfSeats: number | null;
  numberOfSeatsOccupied: number | null;
  applicationPeriodEndDate: string | null;
  imageUri: string | null;
  userStatus: LearningOpportunityUserStatus | null;
  url: string;
  audienceIds: string[] | null;
};

export const mapBrowseLearningOpportunities = (
  data: BrowseLearningOpportunity
): BrowseLearningOpportunities => {
  const typeSpecific = mapTypeSpecificNew(data);
  if (!typeSpecific) throw new Error("invalid type LO type");
  return {
    id: data.id,
    studyPeriodShort: formatPeriod(
      "short",
      getDateOrNull(data.startDate),
      getDateOrNull(data.endDate)
    ),
    studyPeriodLong: formatPeriod(
      "long",
      getDateOrNull(data.startDate),
      getDateOrNull(data.endDate)
    ),
    tags: data.tags,
    title: data.title,
    status: data.status,
    selfPaced: data.selfPaced,
    type: typeSpecific.type,
    hostingUniversityId: data.hostingUniversity
      ? data.hostingUniversity.id
      : null,
    ects: data.ects,
    volumeOfLearning: data.volumeOfLearning,
    startDate: data.startDate,
    studyFormat: data.studyFormat,
    topics:
      data.topics === null
        ? []
        : data.topics.map((x) => ({
            id: x.id,
            name: x.name,
          })),
    topicsWithLevel:
      data.topicsWithLevel === null
        ? []
        : data.topicsWithLevel.map((x) => ({
            topicId: x.topicId,
            levelId: x.levelId,
          })),
    publishedDate: data.publishedDate,
    imageUri: createImageUrl(data.imagesPath, 280, 280),
    maximumNumberOfSeats: data.maximumNumberOfSeats,
    numberOfSeatsOccupied: data.numberOfSeatsOccupied,
    applicationPeriodEndDate: data.applicationEndDate,
    url: buildLearningOpportunityUrl(data.id, data.title),
    userStatus: data.userStatus ?? null,
    audienceIds: data.audienceIds
  };
};

export const getBrowseLearningOpportunities = async (
  request?: IncomingMessage,
  fromDate?: string,
  toDate?: string
): Promise<BrowseLearningOpportunities[]> => {
  const auth = request ? getAuthStatus(request) : undefined;
  const client = createBackendApiClient(auth?.token);
  const data = await client.learningOpportunity_Browse(
    fromDate ? fromDate : null,
    toDate ? toDate : null
  );
  return data.filter((x) => !!x.type).map(mapBrowseLearningOpportunities);
};

export const getAudiences = async (): Promise<Audience2[]> => {
  const client = createBackendApiClient();
  const data = await client.utility_GetAudienceList();
  return data;
};

export type PassportLearningOpportunities = {
  id: string;
  userStatus: LearningOpportunityUserStatus | null;
  ects: number | null;
  title: string | null;
  type: LearningOpportunityType;
  url: string;
  imageUriSmall: string | null;
  studyPeriodIso: string | null;
  hostingUniversityId: string | null;
  hostingUniversityName: string | null;
  studyPeriod: string | null;
  isPastStartDate: boolean | null;
  imageUri: string | null;
  maximumNumberOfSeats: number | null;
  numberOfSeatsOccupied: number | null;
  status: LearningOpportunityStatus | null;
  studyPeriodShort: string | null;
  studyPeriodLong: string | null;
  applicationPeriodEndDate: string | null;
  topics: LearningOpportunityTopic[];
};

export const mapPassportLearningOpportunities = (
  data: PassportLearningOpportunity
) => {
  const typeSpecific = mapTypeSpecificNew(data);
  if (!typeSpecific) throw new Error("invalid type LO type");
  const startDate = getDateOrNull(data.startDate);
  let isPastStartDate: boolean | null = null;
  const now = dayjs();
  if (startDate && now.isAfter(startDate)) {
    isPastStartDate = true;
  }
  return {
    id: data.id,
    userStatus: data.userData?.status ?? null,
    ects: data.ects,
    title: data.title,
    type: typeSpecific.type,
    url: buildLearningOpportunityUrl(data.id, data.title),
    imageUriSmall: createImageUrl(data.imagesPath, 40, 40),
    studyPeriodIso: formatPeriodIso(
      getDateOrNull(data.startDate),
      getDateOrNull(data.endDate)
    ),
    hostingUniversityId: data.hostingUniversity
      ? data.hostingUniversity.id
      : null,
    hostingUniversityName: data.hostingUniversity?.name ?? null,
    studyPeriod: formatPeriod(
      "long",
      getDateOrNull(data.startDate),
      getDateOrNull(data.endDate)
    ),
    isPastStartDate: isPastStartDate,
    imageUri: createImageUrl(data.imagesPath, 280, 280),
    maximumNumberOfSeats: data.maximumNumberOfSeats,
    numberOfSeatsOccupied: data.numberOfSeatsOccupied,
    status: data.status,
    studyPeriodShort: formatPeriod(
      "short",
      getDateOrNull(data.startDate),
      getDateOrNull(data.endDate)
    ),
    studyPeriodLong: formatPeriod(
      "long",
      getDateOrNull(data.startDate),
      getDateOrNull(data.endDate)
    ),
    applicationPeriodEndDate: data.applicationEndDate,
    topics:
      data.topics === null
        ? []
        : data.topics.map((x) => ({
            id: x.id,
            name: x.name,
          })),
  };
};

export const getPassportLearningOpportunities = async (
  request?: IncomingMessage
): Promise<PassportLearningOpportunities[]> => {
  const auth = request ? getAuthStatus(request) : undefined;
  const client = createBackendApiClient(auth?.token);
  const data = await client.learningOpportunity_GetMyForPassport();
  return data.filter((x) => !!x.type).map(mapPassportLearningOpportunities);
};

export type LightWeightLearningOpportunities = {
  id: string | null;
  url: string;
  imageUri: string | null;
  title: string | null;
  studyPeriodShort: string | null;
  studyPeriodLong: string | null;
  type: LearningOpportunityType;
  userAdmissionData: UserAdmissionData | null;
  status: LearningOpportunityStatus | null;
  userStatus: LearningOpportunityUserStatus | null;
  maximumNumberOfSeats: number | null;
  numberOfSeatsOccupied: number | null;
  applicationPeriodEndDate: string | null;
  topics: LearningOpportunityTopic[];
};

export const mapLightWeightLearningOpportunities = (
  data: LightWeightLearningOpportunity
) => {
  const typeSpecific = mapTypeSpecificNew(data);
  if (!typeSpecific) throw new Error("invalid type LO type");
  return {
    id: data.id,
    url: buildLearningOpportunityUrl(data.id ? data.id : "", data.title),
    imageUri: createImageUrl(data.imagesPath, 280, 280),
    title: data.title,
    studyPeriodShort: formatPeriod(
      "short",
      getDateOrNull(data.startDate),
      getDateOrNull(data.endDate)
    ),
    studyPeriodLong: formatPeriod(
      "long",
      getDateOrNull(data.startDate),
      getDateOrNull(data.endDate)
    ),
    type: typeSpecific.type,
    userAdmissionData: mapUserAdmissionData(data.userData),
    status: data.status,
    userStatus: data.userData?.status ?? null,
    maximumNumberOfSeats: data.maximumNumberOfSeats,
    numberOfSeatsOccupied: data.numberOfSeatsOccupied,
    applicationPeriodEndDate: data.applicationEndDate ?? null,
    topics:
      data.topics === null
        ? []
        : data.topics.map((x) => ({
            id: x.id,
            name: x.name,
          })),
  };
};

export const getLightWeightLearningOpportunities = async (
  request?: IncomingMessage
): Promise<LightWeightLearningOpportunities[]> => {
  const auth = request ? getAuthStatus(request) : undefined;
  const client = createBackendApiClient(auth?.token);
  const data = await client.learningOpportunity_GetAllLight();
  return data.filter((x) => !!x.type).map(mapLightWeightLearningOpportunities);
};
